import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Card, FormFieldLabel, Input, StatusMessage } from '@vwfs-bronson/bronson-react';
import { RootState } from '../../services/redux';
import { ChatMessage } from './ChatMessage';
import { ConversationHeader } from './ConversationHeader';
import { GroupedMessageSet } from './useConversations';

interface ConversationProps {
  activeConversation: MilesPosNotesIntegrationService.Components.Schemas.Conversation | null;
  isUpdatingTitle: boolean;
  updateConversationTitle: (conversationId: string, newTitle: string) => Promise<void>;
  updateConversationStatus: (
    conversationId: string,
    newStatus: MilesPosNotesIntegrationService.Components.Schemas.ConversationStatus
  ) => Promise<void>;
  isUpdatingStatus: boolean;
  groupedMessages: GroupedMessageSet[];
  messageInputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
  handleAddMessage: () => void;
  titleOptions: Array<{ value: string; label: string }>;
}

export const Conversation: React.FC<ConversationProps> = ({
  activeConversation,
  isUpdatingTitle,
  updateConversationTitle,
  updateConversationStatus,
  isUpdatingStatus,
  groupedMessages,
  messageInputRef,
  handleAddMessage,
  titleOptions,
}) => {
  const { t } = useTranslation();
  const isSendingMessage = useSelector((state: RootState) => state.notes.isSendingMessage);
  const lastConversationFetchError = useSelector((state: RootState) => state.notes.lastConversationFetchError);
  const hasUnreadMessages = (activeConversation?.unreadMessages ?? 0) > 0 || !activeConversation?.isSeenByCurrentUser;

  if (lastConversationFetchError) {
    return (
      <Card title="" className="u-text-left u-mb">
        <StatusMessage status="error" label={t('conversation:errorFetchingConversation')} />
      </Card>
    );
  }

  if (!activeConversation) {
    return (
      <Card title="" className="u-text-left u-mb">
        <StatusMessage status="info" label={t('conversation:noConversationSelected')} />
      </Card>
    );
  }

  const handleUpdateConversationTitleForHeader = async (newTitle: string) => {
    if (activeConversation?.conversationId) {
      return updateConversationTitle(activeConversation.conversationId, newTitle);
    }
    return Promise.resolve();
  };

  const handleUpdateConversationStatusForHeader = async (
    newStatus: MilesPosNotesIntegrationService.Components.Schemas.ConversationStatus
  ) => {
    if (activeConversation?.conversationId) {
      return updateConversationStatus(activeConversation.conversationId, newStatus);
    }
    return Promise.resolve();
  };

  // Shows unread label only on the last message if there are unread messages
  const shouldShowUnreadLabel = (groupIndex: number, messageIndex: number): boolean => {
    const isLastGroup = groupIndex === groupedMessages.length - 1;
    const isLastMessageOfGroup = messageIndex === groupedMessages[groupIndex].messages.length - 1;

    return hasUnreadMessages && isLastGroup && isLastMessageOfGroup;
  };

  return (
    <>
      <Card title="" className="u-text-left u-mb">
        <ConversationHeader
          activeConversation={activeConversation}
          isUpdatingTitle={isUpdatingTitle}
          updateConversationTitle={handleUpdateConversationTitleForHeader}
          updateConversationStatus={handleUpdateConversationStatusForHeader}
          isUpdatingStatus={isUpdatingStatus}
          titleOptions={titleOptions}
        />

        <section
          style={{ maxHeight: '400px', overflowY: 'auto', scrollBehavior: 'smooth' }}
          ref={(el) => el?.scrollTo(0, el.scrollHeight)}
        >
          {groupedMessages.map((group, groupIndex) => (
            <React.Fragment key={group.participantId + (group.messages[0]?.messageId || '')}>
              {group.messages.map((message, index) => {
                const shouldRenderUnreadLabel = shouldShowUnreadLabel(groupIndex, index);

                return (
                  <React.Fragment key={message.messageId}>
                    {shouldRenderUnreadLabel && (
                      <div className="u-text-center u-mv u-font-size-fs-1">{t('conversation:unreadMessagesCount')}</div>
                    )}

                    <ChatMessage
                      key={message.messageId}
                      author={group.author}
                      message={message.message ?? ''}
                      time={new Date(message.timestamp ?? '')}
                      userRole={group.userRole}
                      isCurrentUser={group.isCurrentUser}
                      showHeader={index === 0}
                      messageType={message.type || 'TEXT_MESSAGE'}
                    />
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}
        </section>
      </Card>

      <FormFieldLabel>{t('conversation:typeMessage')}</FormFieldLabel>
      <Input
        ref={messageInputRef as React.RefObject<HTMLInputElement>}
        className="u-mb"
        addonClickHandler={handleAddMessage}
        addonElement="button"
        addonText={isSendingMessage ? t('conversation:sending') : t('conversation:send')}
        placeholder={activeConversation?.status === 'CLOSE' ? t('conversation:conversationLocked') : ''}
        disabled={activeConversation?.status === 'CLOSE' || isSendingMessage}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAddMessage();
          }
        }}
      />
    </>
  );
};
